import React from 'react';
import Label from 'src/modules/shared/components/atoms/inputs/label';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import { type CustomFieldEntityType, CustomFieldType } from 'src/api/generated';
import { type CustomField } from 'src/modules/communications/types/custom-fields.type';
import {
  formatCustomFieldValue,
  parseCustomFieldValue,
} from 'src/modules/shared/utils/custom-fields.util';
import {
  formatLegacyAwareCustomFieldDateInputValue,
  parseLegacyAwareCustomFieldDateInputValue,
} from 'src/modules/shared/utils/custom-field-date-legacy.util';
import { CustomFieldInlineFileInput } from 'src/modules/communications/components/molecules/custom-fields/custom-field-inline-file-input';

interface CustomFieldFormFieldProps {
  field: CustomField;
  onChange: (value: unknown) => void;
  disabled?: boolean;
  loading?: boolean;
  entityType?: CustomFieldEntityType;
  entityId?: number;
}

export const CustomFieldFormField: React.FC<CustomFieldFormFieldProps> = ({
  field,
  onChange,
  disabled = false,
  loading = false,
  entityType,
  entityId,
}) => {
  const isDisabled = disabled || loading;
  const stringValue = formatCustomFieldValue(field.value, field.type);

  const handleStringChange = (value: string) => {
    if (field.type === CustomFieldType.DATE) {
      onChange(
        parseLegacyAwareCustomFieldDateInputValue(
          value,
          Boolean(field.includeTime)
        )
      );
      return;
    }

    const parsed = parseCustomFieldValue(value, field.type);
    onChange(parsed);
  };

  const dateInputValue =
    field.type === CustomFieldType.DATE
      ? formatLegacyAwareCustomFieldDateInputValue(
          stringValue,
          Boolean(field.includeTime)
        )
      : stringValue;

  if (field.type === CustomFieldType.BOOLEAN) {
    const booleanOptions = [
      // TODO: check when implementing i18n
      { value: 'true', content: 'Sí' },
      { value: 'false', content: 'No' },
    ];

    const current =
      stringValue === 'true' || stringValue === 'false' ? stringValue : '';

    return (
      <div className="flex flex-col gap-1">
        <Label size="md">{field.name}</Label>
        <Select
          value={current}
          onChange={(e) => handleStringChange(e.target.value)}
          options={[
            // TODO: check when implementing i18n
            { value: '', content: 'Selecciona una opción' },
            ...booleanOptions,
          ]}
          disabled={isDisabled}
        />
      </div>
    );
  }

  if (field.type === CustomFieldType.LIST) {
    const options =
      field.options?.map((option) => ({
        value: option.value,
        content: option.label,
      })) ?? [];

    return (
      <div className="flex flex-col gap-1">
        <Label size="md">{field.name}</Label>
        <Select
          value={stringValue || ''}
          onChange={(e) => handleStringChange(e.target.value)}
          options={[
            // TODO: check when implementing i18n
            { value: '', content: 'Selecciona una opción' },
            ...options,
          ]}
          disabled={isDisabled}
        />
      </div>
    );
  }

  if (field.type === CustomFieldType.FILE) {
    if (!entityType || !entityId) {
      return null;
    }

    return (
      <div className="flex flex-col gap-1">
        <Label size="md">{field.name}</Label>
        <CustomFieldInlineFileInput
          label={field.name}
          value={stringValue}
          onSave={(next) => {
            const parsed = parseCustomFieldValue(next, field.type);
            onChange(parsed);
            return Promise.resolve();
          }}
          disabled={isDisabled}
          loading={loading}
          entityType={entityType}
          entityId={entityId}
        />
      </div>
    );
  }

  const inputType =
    field.type === CustomFieldType.NUMBER
      ? 'number'
      : field.type === CustomFieldType.DATE
        ? field.includeTime
          ? 'datetime-local'
          : 'date'
        : 'text';

  return (
    <div className="flex flex-col gap-1">
      <Label size="md">{field.name}</Label>
      <TextInput
        type={inputType}
        value={dateInputValue}
        onChange={(e) => handleStringChange(e.target.value)}
        disabled={isDisabled}
      />
    </div>
  );
};
